<template>
    <div class="sod-eod-workload-calendar">
        <div class="d-flex align-center mb-2">
            <div class="pulse-title">
                <v-icon size="16" color="grey darken-1" class="mr-1">mdi-calendar-blank-outline</v-icon>
                EOD Workload Pulse
            </div>
            <v-spacer />
        </div>
        <div class="d-flex align-center justify-center mb-2">
            <v-btn icon small class="nav-btn" @click="goToPrevMonth"><v-icon small>mdi-chevron-left</v-icon></v-btn>
            <span class="font-weight-bold pulse-month-label">
                {{ monthLabel }}
            </span>
            <v-btn icon small class="nav-btn" @click="goToNextMonth"><v-icon small>mdi-chevron-right</v-icon></v-btn>
        </div>
        <v-btn v-if="!isCurrentMonth" text x-small color="primary" block class="pulse-today-btn mb-2" @click="goToCurrentMonth">
            Jump to today
        </v-btn>

        <div class="d-flex" style="gap: 4px;">
            <div v-for="w in weekdayLabels" :key="w" class="pulse-weekday-label">{{ w }}</div>
        </div>
        <div class="pulse-calendar-grid mt-1">
            <div v-for="n in leadingBlanks" :key="`b-${n}`" class="pulse-cal-blank"></div>
            <v-tooltip v-for="iso in monthDays" :key="iso" top max-width="220" :disabled="!entryFor(iso)">
                <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" class="pulse-cal-cell" :class="cellClass(iso)">
                        <span class="pulse-cal-daynum">{{ dayNumOf(iso) }}</span>
                    </div>
                </template>
                <div v-if="entryFor(iso)">
                    <div class="font-weight-bold">{{ formatDate(iso) }}</div>
                    <div v-if="entryFor(iso).eod">
                        Workload: {{ pulseLabel(entryFor(iso).eod.workload_pulse) }}<br />
                        Completed {{ entryFor(iso).eod.completed_count ?? 0 }} of {{ entryFor(iso).eod.planned_count ?? 0 }} planned
                    </div>
                    <div v-else-if="entryFor(iso).sod">No EOD submitted (SOD only)</div>
                </div>
            </v-tooltip>
        </div>

        <div class="pulse-legend-row mt-3">
            <span class="d-inline-flex align-center" style="gap:6px;">
                <span class="pulse-dot pulse-comfortable"></span> Comfortable
            </span>
            <span class="d-inline-flex align-center" style="gap:6px;">
                <span class="pulse-dot pulse-about_right"></span> About Right
            </span>
            <span class="d-inline-flex align-center" style="gap:6px;">
                <span class="pulse-dot pulse-too_much"></span> Too Much
            </span>
            <span class="d-inline-flex align-center" style="gap:6px;">
                <span class="pulse-dot pulse-no-eod"></span> No EOD
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SodEodWorkloadCalendar',
    props: {
        entries: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        const now = new Date();
        return {
            curYear: now.getFullYear(),
            curMonth: now.getMonth(), // 0-indexed
            weekdayLabels: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
        };
    },
    computed: {
        entriesByDate() {
            const map = {};
            (this.entries || []).forEach((e) => {
                if (e && e.work_date) map[e.work_date] = e;
            });
            return map;
        },
        monthDays() {
            const total = new Date(this.curYear, this.curMonth + 1, 0).getDate();
            const days = [];
            for (let d = 1; d <= total; d += 1) {
                days.push(`${this.curYear}-${String(this.curMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
            }
            return days;
        },
        leadingBlanks() {
            const first = this.monthDays[0];
            if (!first) return 0;
            const [y, m, d] = first.split('-').map(Number);
            const dow = new Date(y, m - 1, d).getDay(); // 0=Sun..6=Sat
            return (dow + 6) % 7; // Monday-first
        },
        monthLabel() {
            return new Date(this.curYear, this.curMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        },
        isCurrentMonth() {
            const now = new Date();
            return this.curYear === now.getFullYear() && this.curMonth === now.getMonth();
        },
    },
    methods: {
        goToPrevMonth() {
            if (this.curMonth === 0) {
                this.curMonth = 11;
                this.curYear -= 1;
            } else {
                this.curMonth -= 1;
            }
        },
        goToNextMonth() {
            if (this.curMonth === 11) {
                this.curMonth = 0;
                this.curYear += 1;
            } else {
                this.curMonth += 1;
            }
        },
        goToCurrentMonth() {
            const now = new Date();
            this.curYear = now.getFullYear();
            this.curMonth = now.getMonth();
        },
        entryFor(iso) {
            return this.entriesByDate[iso] || null;
        },
        dayNumOf(iso) {
            return Number(iso.split('-')[2]);
        },
        cellClass(iso) {
            const entry = this.entryFor(iso);
            if (!entry) return 'pulse-cal-empty';
            if (entry.eod && entry.eod.workload_pulse) {
                return `pulse-cal-${entry.eod.workload_pulse}`;
            }
            if (entry.sod) return 'pulse-cal-no-eod';
            return 'pulse-cal-empty';
        },
        pulseLabel(pulse) {
            return { too_much: 'Too Much', about_right: 'About Right', comfortable: 'Comfortable' }[pulse] || pulse;
        },
        formatDate(iso) {
            return this.$date(iso).format('ddd, MMM D, YYYY');
        },
    },
};
</script>

<style scoped>
.pulse-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
}

.nav-btn {
    background: rgba(0, 0, 0, 0.04);
}

.pulse-month-label {
    font-size: 0.95rem;
    min-width: 150px;
    text-align: center;
}

.pulse-today-btn {
    font-size: 11px;
    height: 22px !important;
}

.pulse-weekday-label {
    flex: 1;
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.45);
    letter-spacing: 0.03em;
}

.pulse-calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
}

.pulse-cal-blank {
    aspect-ratio: 1 / 1;
}

.pulse-cal-cell {
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 600;
    cursor: default;
}

.pulse-cal-daynum {
    opacity: 0.85;
}

.pulse-cal-empty {
    background: rgba(0, 0, 0, 0.03);
    color: rgba(0, 0, 0, 0.35);
}

.pulse-cal-no-eod {
    background: rgba(0, 0, 0, 0.07);
    color: rgba(0, 0, 0, 0.55);
}

.pulse-cal-comfortable {
    background: rgba(22, 163, 74, 0.16);
    color: #16803c;
}

.pulse-cal-about_right {
    background: rgba(245, 178, 25, 0.22);
    color: #92620a;
    outline: 2px solid rgba(245, 178, 25, 0.55);
    outline-offset: -2px;
}

.pulse-cal-too_much {
    background: rgba(229, 72, 77, 0.16);
    color: #c0292d;
}

.pulse-legend-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.6);
}

.pulse-dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
}

.pulse-dot.pulse-comfortable {
    background: #16a34a;
}

.pulse-dot.pulse-about_right {
    background: #f5b219;
}

.pulse-dot.pulse-too_much {
    background: #e5484d;
}

.pulse-dot.pulse-no-eod {
    background: rgba(0, 0, 0, 0.25);
}
</style>
